import apiClient from "@/api/client";
import type { Task, TaskDetail, TaskFormData, TaskFilters } from "@/features/tasks/types";


const wrap = <T>(res: { data: { data: T } }) => res.data.data;

export const tasksApi = {
  list: (filters?: TaskFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      const { assigned_to, project, client, category, no_category, include_archived, ...rest } = filters;
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
      });
      if (assigned_to && assigned_to.length > 0) {
        assigned_to.forEach((id) => params.append("assigned_to", id));
      }
      if (project && project.length > 0) {
        project.forEach((id) => params.append("project", id));
      }
      if (client && client.length > 0) {
        client.forEach((id) => params.append("client", id));
      }
      if (category) params.append("category", category);
      if (no_category) params.append("no_category", "true");
      if (include_archived) params.append("include_archived", "true");
    }
    return apiClient
      .get<{ data: Task[] }>("/tasks/", { params })
      .then(wrap);
  },

  get: (id: string) =>
    apiClient.get<{ data: TaskDetail }>(`/tasks/${id}/`).then(wrap),

  create: (data: TaskFormData) =>
    apiClient.post<{ data: Task }>("/tasks/", data).then(wrap),

  update: (id: string, data: Partial<TaskFormData>) =>
    apiClient.patch<{ data: Task }>(`/tasks/${id}/`, data).then(wrap),

  delete: (id: string) => apiClient.delete(`/tasks/${id}/`),

  assign: (id: string, userId: string) =>
    apiClient.post<{ data: TaskDetail }>(`/tasks/${id}/assign/`, { user_id: userId }).then(wrap),

  unassign: (id: string, userId: string) =>
    apiClient.post<{ data: TaskDetail }>(`/tasks/${id}/unassign/`, { user_id: userId }).then(wrap),

  watch: (id: string) =>
    apiClient.post<{ data: { is_watching: boolean } }>(`/tasks/${id}/watch/`, {}).then(wrap),

  unwatch: (id: string) =>
    apiClient.post<{ data: { is_watching: boolean } }>(`/tasks/${id}/unwatch/`, {}).then(wrap),

  cloneBulk: (task_ids: string[], target_project_id: string) =>
    apiClient
      .post<{ data: { cloned_count: number; task_ids: string[] } }>("/tasks/clone-bulk/", { task_ids, target_project_id })
      .then(wrap),

  pin: (id: string) =>
    apiClient
      .post<{ data: { is_pinned: boolean } }>(`/tasks/${id}/pin/`, {})
      .then(wrap),

  archive: (id: string) =>
    apiClient
      .post<{ data: { is_archived: boolean } }>(`/tasks/${id}/archive/`, {})
      .then(wrap),

  unarchive: (id: string) =>
    apiClient
      .post<{ data: { is_archived: boolean } }>(`/tasks/${id}/unarchive/`, {})
      .then(wrap),

  reorder: (ids: string[]) =>
    apiClient.post("/tasks/reorder/", { ids }),

  setExpanded: (id: string, is_expanded: boolean) =>
    apiClient.post(`/tasks/${id}/set-expanded/`, { is_expanded }),

  groupStates: () =>
    apiClient.get<{ data: string[] }>("/tasks/group-states/").then(wrap),

  setGroupCollapsed: (project_key: string, is_collapsed: boolean) =>
    apiClient.post("/tasks/set-group-collapsed/", { project_key, is_collapsed }),

  getUIPrefs: () =>
    apiClient
      .get<{ data: { filters_hidden: boolean; kanban_hidden: boolean; table_panel_hidden: boolean } }>("/tasks/ui-prefs/")
      .then(wrap),

  setUIPrefs: (prefs: Partial<{ filters_hidden: boolean; kanban_hidden: boolean; table_panel_hidden: boolean }>) =>
    apiClient.post("/tasks/set-ui-prefs/", prefs),
};
